/**
 * QuickAddBody — the title textarea + AI suggestion banner +
 * dynamic secondary field of the QuickAdd modal.
 *
 * The textarea is auto-focused on open; the suggestion chip only
 * shows when the AI heuristic picked a type AND the user hasn't
 * explicitly chosen one. The secondary field is type-driven —
 * absent for the default Task case, present for payments
 * (amount), calls (contact), reading (URL), etc.
 */
import { Sparkles } from 'lucide-react';
import { TYPE_BY_KEY } from './typeConfig';

export function QuickAddBody({
  inputRef,
  title,
  onTitleChange,
  suggestion,
  showSuggestion,
  onAcceptSuggestion,
  secondary,
}) {
  return (
    <div className="px-5 py-4">
      <textarea
        ref={inputRef}
        data-testid="quickadd-title"
        value={title}
        onChange={(e) => onTitleChange(e.target.value)}
        placeholder="What's on your mind? (Enter to save, Cmd+Enter explicit, Shift+Enter newline)"
        rows={2}
        className="w-full px-3 py-2.5 rounded-xl text-[14px] outline-none resize-none leading-snug"
        style={{
          border: '1px solid var(--pd-border)',
          color: 'var(--pd-text)',
          background: 'var(--pd-panel)',
        }}
      />

      {showSuggestion ? (
        <button
          type="button"
          data-testid="quickadd-suggestion"
          onClick={onAcceptSuggestion}
          className="mt-2 flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] font-medium"
          style={{
            background: 'var(--pd-accent-soft)',
            color: 'var(--pd-accent)',
            border: '1px solid var(--pd-accent)',
          }}
        >
          <Sparkles className="w-3 h-3" />
          Looks like a <span className="font-semibold">{TYPE_BY_KEY[suggestion.type]?.label}</span> — accept?
        </button>
      ) : null}

      {secondary ? (
        <div className="mt-3">
          <label
            className="text-[10px] uppercase tracking-wider font-semibold block mb-1"
            style={{ color: 'var(--pd-subtle)' }}
          >
            {secondary.label}
          </label>
          <input
            data-testid={secondary.testid}
            type={secondary.type || 'text'}
            value={secondary.value}
            onChange={(e) => secondary.onChange(e.target.value)}
            placeholder={secondary.placeholder}
            className="w-full px-3 py-2 rounded-lg text-[12px] outline-none"
            style={{
              border: '1px solid var(--pd-border)',
              color: 'var(--pd-text)',
              background: 'var(--pd-panel)',
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
